import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Head from 'react-helmet';

import Container from 'app/shared/core/Container';
import Button from 'app/shared/core/Button';
import Linker from 'app/shared/modules/Linker';
import ListingWrapper from 'app/shared/modules/listing-cards/ListingWrapper';
import ReviewForm from 'app/shared/modules/ReviewForm';
import ReviewActions from 'app/shared/flux/actions/ReviewActions';
import FetchListingActions from 'app/shared/flux/actions/FetchListingActions';
import api from 'app/shared/utils/api';
import pathsManifest from 'app/shared/utils/pathsManifest';
import type { RootReduxState } from 'app/shared/flux/reducers';

type AutocompleteSuggestion = {
  name: string;
  lotIdEncoded?: string;
};

type ReviewListing = {
  displayName: string;
  aliasEncoded: string;
};
import {
  completedContainer,
  completedMessage,
  completedTitle,
  errorText,
  listingSection,
  loadingText,
  loginPrompt,
  loginText,
  pageContainer,
  pageSubtitle,
  pageTitle,
  searchInput,
  searchSection,
  suggestionItem,
  suggestionItemActive,
  suggestionsDropdown,
} from './reviewHomePage.styles';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AppDispatch = (...args: Array<any>) => any;

export default function ReviewHomePage() {
  const dispatch: AppDispatch = useDispatch();
  const loggedIn = useSelector((state: RootReduxState) => state.user.loggedIn);

  const [step, setStep] = useState<'search' | 'review' | 'completed'>('search');
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Array<AutocompleteSuggestion>>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [listing, setListing] = useState<ReviewListing | null>(null);
  const [isLoadingListing, setIsLoadingListing] = useState(false);
  const [fetchError, setFetchError] = useState('');

  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suggestionsRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    return () => {
      dispatch(ReviewActions.resetReview());
    };
  }, [dispatch]);

  const fetchSuggestions = useCallback(
    (partial: string) => {
      (
        dispatch(api.area.autocomplete({ partial, limit: 5, types: 'address' } as any)) as Promise<{
          data?: Array<AutocompleteSuggestion>;
        }>
      )
        .then((results) => {
          const addressResults = (results.data || []).filter((item) => item.lotIdEncoded);
          setSuggestions(addressResults);
          setShowSuggestions(addressResults.length > 0);
          setActiveIndex(-1);
        })
        .catch(() => {
          setSuggestions([]);
          setShowSuggestions(false);
        });
    },
    [dispatch],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setFetchError('');

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (value.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 300);
  };

  const selectSuggestion = (suggestion: AutocompleteSuggestion) => {
    setShowSuggestions(false);
    setQuery(suggestion.name);
    setActiveIndex(-1);

    if (!suggestion.lotIdEncoded) {
      setFetchError('Could not find a listing for this address. Please try another address.');
      return;
    }

    setIsLoadingListing(true);
    setFetchError('');

    (
      dispatch(
        FetchListingActions.fetchListingByMaloneLotIdNoRedirect({
          maloneLotIdEncoded: suggestion.lotIdEncoded,
          isBuilding: false,
        } as any),
      ) as Promise<ReviewListing | null>
    )
      .then((result) => {
        setIsLoadingListing(false);
        if (result) {
          setListing(result);
          setStep('review');
          dispatch(ReviewActions.resetReview());
        } else {
          setFetchError('We could not find a listing at this address on HotPads. Please try another address.');
        }
      })
      .catch(() => {
        setIsLoadingListing(false);
        setFetchError('Something went wrong. Please try again.');
      });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < suggestions.length) {
          selectSuggestion(suggestions[activeIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setActiveIndex(-1);
        break;
    }
  };

  const handleBlur = () => {
    // Delay to allow click on suggestion to fire
    setTimeout(() => setShowSuggestions(false), 200);
  };

  const handleSearchAgain = () => {
    setStep('search');
    setListing(null);
    setQuery('');
    setFetchError('');
    dispatch(ReviewActions.resetReview());
  };

  if (step === 'completed') {
    return (
      <Container className={pageContainer}>
        <Head title="Review Complete | HotPads" />
        <div className={completedContainer}>
          <div className={completedTitle}>Thank you!</div>
          <div className={completedMessage}>Your review helps fellow renters find their next home.</div>
          <Linker to={pathsManifest.FrontPage}>
            <Button btnType="primary">Back to HotPads</Button>
          </Linker>
        </div>
      </Container>
    );
  }

  if (step === 'review' && listing) {
    const returnUrl = encodeURIComponent('/review-home');

    if (!loggedIn) {
      return (
        <Container className={pageContainer}>
          <Head title={`Review ${listing.displayName} | HotPads`} />
          <div className={pageTitle}>Review an apartment building</div>
          <div className={listingSection}>
            {/* @ts-expect-error ListingWrapper is a legacy connected component; required props come from Redux connect() */}
            <ListingWrapper listing={listing} />
          </div>
          <div className={loginPrompt}>
            <div className={loginText}>Please sign in to leave a review.</div>
            <Linker to={`${pathsManifest.LoginPage}?redirect=${returnUrl}`}>
              <Button btnType="primary">Sign In</Button>
            </Linker>
          </div>
        </Container>
      );
    }

    return (
      <Container className={pageContainer}>
        <Head title={`Review ${listing.displayName} | HotPads`} />
        <div className={pageTitle}>Review an apartment building</div>
        <div className={pageSubtitle}>
          Your reviews help renters make informed decisions and help property managers improve their services.
        </div>
        <div className={listingSection}>
          {/* @ts-expect-error ListingWrapper is a legacy connected component; required props come from Redux connect() */}
          <ListingWrapper listing={listing} />
        </div>
        <ReviewForm
          listingAlias={listing.aliasEncoded}
          onSubmitSuccess={() => setStep('completed')}
          reviewType="myHome"
          positiveTags={[
            'Great neighborhood',
            'Feels safe',
            'Quiet and peaceful',
            'Responsive management',
            'Good value for rent',
          ]}
          negativeTags={[
            'Noisy area',
            'Feels unsafe',
            'Unresponsive management',
            'Not worth the rent',
            'Needs repairs',
          ]}
        />
        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <Button btnType="text" onClick={handleSearchAgain}>
            Search for a different address
          </Button>
        </div>
      </Container>
    );
  }

  const activeSuggestionId = activeIndex >= 0 ? `review-home-suggestion-${activeIndex}` : undefined;

  return (
    <Container className={pageContainer}>
      <Head title="Review an Apartment Building | HotPads" />
      <div className={pageTitle}>Review an apartment building</div>
      <div className={pageSubtitle}>
        Share the truth about an apartment you currently or previously lived in. Future tenants are counting on your
        honest take!
      </div>
      <div className={searchSection}>
        <input
          ref={inputRef}
          type="text"
          className={searchInput}
          placeholder="Enter your address..."
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onFocus={() => {
            if (suggestions.length > 0) setShowSuggestions(true);
          }}
          role="combobox"
          aria-expanded={showSuggestions}
          aria-controls="review-home-suggestions"
          aria-activedescendant={activeSuggestionId}
          aria-autocomplete="list"
          aria-label="Search for your address"
          autoComplete="off"
        />
        {showSuggestions && suggestions.length > 0 && (
          <ul
            id="review-home-suggestions"
            ref={suggestionsRef}
            className={suggestionsDropdown}
            role="listbox"
            aria-label="Address suggestions"
          >
            {suggestions.map((item, index) => (
              <li
                key={item.lotIdEncoded ?? index}
                id={`review-home-suggestion-${index}`}
                className={`${suggestionItem} ${index === activeIndex ? suggestionItemActive : ''}`}
                role="option"
                aria-selected={index === activeIndex}
                onClick={() => selectSuggestion(item)}
              >
                {item.name}
              </li>
            ))}
          </ul>
        )}
      </div>
      {isLoadingListing && <div className={loadingText}>Looking up your listing...</div>}
      {fetchError && (
        <div className={errorText} role="alert">
          {fetchError}
        </div>
      )}
    </Container>
  );
}
